import { FormEvent, useEffect, useRef, useState } from 'react';
import { Button } from '@shared/components/Button';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { i18n } from '@shared/util/i18n';
import { MBSentry } from '@shared/util/sentry';
import styles from './NameForm.module.css';

/**
 * Menu.NameForm
 *
 * A single-field form for naming a thing, for use in create/rename/duplicate
 * menus. Owns the submit lifecycle — loading state, and gating both the button
 * and the Enter key on `canSave` — and nothing else.
 *
 * The field autofocuses with its contents selected, so typing replaces the existing or
 * suggested name.
 *
 * The caller owns the value, the error message, and dismissal: closing the menu
 * (or advancing a wizard step, or navigating) belongs in `onSave`, so a failed
 * save simply leaves the form open. Handle failures there too — a rejection that
 * escapes `onSave` is treated as a bug and reported, not shown to the user.
 *
 * ```tsx
 * const { close } = useMenuContext();
 * const [name, setName] = useState(view.name);
 * const [error, setError] = useState<string>();
 *
 * const onSave = async (value: string) => {
 *   try {
 *     await renameView(value);
 *     close();
 *   } catch {
 *     setError(i18n('errors.somethingWentWrong'));
 *   }
 * };
 *
 * <Menu.NameForm
 *   label="Rename view"
 *   value={name}
 *   setValue={setName}
 *   error={error}
 *   onSave={onSave}
 *   canSave={!isEmpty(name.trim()) && name !== view.name}
 *   buttonLabel="Rename"
 * />
 * ```
 *
 * Validation that is knowable before submit belongs in `canSave` rather than in
 * `onSave`, so an invalid value never reaches the mutation.
 */

interface NameFormProps {
  label?: string;
  value: string;
  setValue: (value: string) => void;
  placeholder?: string;
  buttonLabel?: string;
  // Gates the submit button and Enter-key submission. Defaults to true.
  canSave?: boolean;
  // Show an error message below the input.
  error?: string;
  // Closing the menu (and reporting failures) is the callback’s job.
  onSave: (value: string) => Promise<void>;
}

export function NameForm({
  label,
  value,
  setValue,
  placeholder,
  canSave = true,
  buttonLabel,
  error,
  onSave,
}: NameFormProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  // auto-select the input value on mount
  useEffect(() => {
    inputRef.current?.select();
  }, []);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // A form with no submit button still submits on Enter, so the button being
    // hidden is not enough to keep an invalid or in-flight value out of onSave.
    if (!canSave || isSaving) {
      return;
    }
    setIsSaving(true);
    try {
      await onSave(value);
    } catch (e) {
      // onSave is expected to report its own failures and resolve, so anything
      // reaching here is a bug in the caller. We catch and report anyway to
      // prevent an unhandled rejection.
      MBSentry.exception({ error: e instanceof Error ? e : new Error(String(e)) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className={styles.nameForm}>
      <Menu.Section title={label}>
        <View gap={2}>
          <View gap={1}>
            <input
              ref={inputRef}
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder={placeholder}
              autoFocus
            />
            {error && (
              <Text size="body2" danger pretty>
                {error}
              </Text>
            )}
          </View>
          {(canSave || isSaving) && (
            <Button
              variant="primary"
              size="jumbo"
              type="submit"
              label={buttonLabel ?? i18n('common.rename')}
              loading={isSaving}
            />
          )}
        </View>
      </Menu.Section>
    </form>
  );
}
